import { DialogDescription } from '@radix-ui/react-dialog';
import { AxiosError } from 'axios';
import { Eye, Settings, FileText } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';

import { PayloadCard } from './PayloadCard';
import { PromptPreviewCard } from './PromptPreviewCard';
import { QueryParamsCard } from './QueryParamsCard';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useRenderPrompt } from '@/hooks/useRenderPrompt';
import {
  Prompt,
  RenderPromptDto,
  RenderPromptResponse,
} from '@/services/PromptsService';

interface PreviewPromptModalProps {
  trigger?: React.ReactNode;
  prompt: Prompt;
  open?: boolean;
  onClose?: () => void;
}

export function PreviewPromptForm({
  trigger,
  prompt,
  open: controlledOpen,
  onClose: controlledOnClose,
}: PreviewPromptModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('params');
  const [previewPrompt, setPreviewPrompt] =
    useState<RenderPromptResponse | null>(null);
  const [queryParams, setQueryParams] = useState<
    Record<string, string | number | boolean>
  >({});
  const [payload, setPayload] = useState<RenderPromptDto>({
    promptName: prompt?.promptName,
    serviceName: prompt?.serviceName,
    customerId: prompt?.customerId ?? undefined,
    version: prompt?.version,
  });

  const { renderPrompt, isLoading } = useRenderPrompt();

  async function onSubmit() {
    try {
      const renderedPrompt = await renderPrompt(payload);
      setPreviewPrompt(renderedPrompt);
      setActiveTab('preview');
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to preview prompt. Please try again.',
        {
          duration: 10000, // 10 segundos
        },
      );
    }
  }

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onClose = controlledOnClose || (() => setInternalOpen(false));
  const setIsOpen = controlledOnClose ? controlledOnClose : setInternalOpen;

  function handleCopyPayload() {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Payload copied to clipboard');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCopyPreview() {
    if (previewPrompt?.renderedTemplate) {
      navigator.clipboard.writeText(previewPrompt.renderedTemplate);
      toast.success('Preview copied to clipboard');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogDescription></DialogDescription>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2 bg-transparent">
            <Eye className="h-4 w-4" />
            Preview
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex max-h-[80vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader className="pb-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            Preview Prompt
            <Badge variant="outline" className="ml-2">
              {prompt?.serviceName}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="params" className="gap-2">
              <Settings className="h-4 w-4" />
              Parameters
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <FileText className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <div className="mt-4 flex-1 overflow-y-auto pr-2">
            <TabsContent value="params" className="space-y-4">
              <QueryParamsCard
                onPayload={setPayload}
                prompt={prompt}
                queryParams={queryParams}
                onQueryParamsChange={setQueryParams}
              />
              <PayloadCard
                payload={payload}
                onCopy={handleCopyPayload}
                copied={copied}
              />
            </TabsContent>

            <TabsContent value="preview" className="mt-0">
              <PromptPreviewCard
                onCopy={handleCopyPreview}
                previewPrompt={previewPrompt as RenderPromptResponse}
                copied={copied}
              />
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={onSubmit} className="gap-2" disabled={isLoading}>
              {isLoading ? 'Previewing...' : 'Preview Prompt'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
